import * as React from "react";
import {
  ComboBox as AriaComboBox,
  ComboBoxStateContext,
  Input as AriaInput,
  InputContext,
  LabelContext,
  type ListBoxProps,
  useSlottedContext,
} from "react-aria-components";
import { wrapper } from "./ComboBox.css.ts";
import { ListBox } from "./ListBox.tsx";
import { Popover, type PopoverWidth } from "./Popover.tsx";
import { FCTextFieldContext } from "./TextFieldContext.ts";
import { ControlledSpinner } from "./internal/ControlledSpinner.tsx";
import { ChevronButton } from "./shared/ChevronButton.tsx";
import { ClearButton } from "./shared/ClearButton.tsx";
import ErrorIcon from "./shared/ErrorIcon.tsx";
import {
  InputBuilder,
  type InputBuilderProps,
  type RenderState,
} from "./shared/InputBuilder.tsx";
import { extractInputBuilderProps } from "./shared/inputBuilderHelper.ts";
import type {
  ComboBoxBaseProps,
  MinimumObject,
  MultiSelectionMode,
  OmittedReactAriaProps,
} from "./types.ts";

type BaseProps<
  Item extends MinimumObject,
  SelectionMode extends MultiSelectionMode = "single",
> = Omit<ComboBoxBaseProps<Item, SelectionMode>, OmittedReactAriaProps> &
  InputBuilderProps;

// "unbounded" doesn't work while virtualized: the rows are absolutely
// positioned, so a content-sized popover has no width to size against and
// collapses. This union rejects the combination at compile time — the strict
// member covers every `virtualized` value that isn't provably `false`. Only
// these two props live in the union: widening it would break `Item` inference
// from `children`/`defaultItems`. Pinned by ComboBox.tst.tsx.
type VirtualizedWidthProps =
  | {
      /** Enable virtualized rendering for large lists. */
      virtualized?: false;

      /**
       * Popover width. Defaults to matching the trigger; "unbounded" for
       * content-sized, or a size token.
       */
      popoverWidth?: PopoverWidth;
    }
  | {
      /** Enable virtualized rendering for large lists. */
      virtualized?: boolean;

      /**
       * Popover width. Defaults to matching the trigger, or a size token. Not
       * "unbounded" while `virtualized` — virtualized rows are absolutely
       * positioned, so they never establish a width for a content-sized
       * popover to size against.
       */
      popoverWidth?: Exclude<PopoverWidth, "unbounded">;
    };

interface OwnProps<
  Item extends MinimumObject,
  SelectionMode extends MultiSelectionMode = "single",
> extends BaseProps<Item, SelectionMode> {
  /**
   * Placeholder text shown when no value is selected.
   */
  placeholder?: string;

  /**
   * Title text for the input, populate this with a string to provide a tooltip
   */
  title?: string;

  children: ListBoxProps<Item>["children"];

  /**
   * If a "clear" button should be displayed when a selection is made.
   * If a string is passed, it will be used as the aria-label for the button.
   */
  clearButton?: boolean | string;

  /**
   * If the combobox is part of a parent that is animating in or out (typically
   * contained within a modal), this should be the duration of said animation.
   * Without the duration, the resize observer will measure the width of the
   * input too early.
   *
   * @deprecated This is no longer necessary, as the ComboBox now waits for any
   * running transitions to complete before measuring the width of the input.
   */
  animationDuration?: number;
}

export type Props<
  Item extends MinimumObject,
  SelectionMode extends MultiSelectionMode = "single",
> = OwnProps<Item, SelectionMode> & VirtualizedWidthProps;

function RightIcons({
  isError,
  isOpen,
  loadingState,
  loadingRenderState,
  loadingStartAnimation,
  loadingOnSuccessAnimationEnd,
}: RenderState) {
  const selectionState = React.use(ComboBoxStateContext);

  if (isError) {
    if (!isOpen) {
      return <ErrorIcon />;
    }

    const hasNoValue =
      selectionState?.value == null ||
      (Array.isArray(selectionState.value) &&
        selectionState.value.length === 0);

    if (hasNoValue) {
      return <ErrorIcon />;
    }
  }

  return (
    <>
      {loadingState == null ? null : (
        <ControlledSpinner
          renderState={loadingRenderState}
          startAnimation={loadingStartAnimation}
          onSuccessAnimationEnd={loadingOnSuccessAnimationEnd}
          size="s"
          color="royalBlue"
        />
      )}
    </>
  );
}

/**
 * A searchable dropdown that allows filtering options by typing.
 * Supports async loading states, clear button, and virtualized lists.
 */
export function ComboBox<
  Item extends MinimumObject,
  SelectionMode extends MultiSelectionMode = "single",
>({
  children,
  placeholder,
  title,
  clearButton = false,
  virtualized = false,
  popoverWidth,
  ...props
}: Props<Item, SelectionMode>): React.ReactElement {
  const inputGroupRef = React.useRef<HTMLDivElement>(null);
  const { inputBuilderProps, restProps } = extractInputBuilderProps(props);

  // We get the label and id of the input from the TextField context. This is required for the accessibility of the combobox.
  const labelProps = useSlottedContext(LabelContext);
  const inputProps = useSlottedContext(InputContext);
  const {
    name,
    textFieldProps: { isInvalid, ...textFieldProps },
  } = React.use(FCTextFieldContext);

  return (
    <AriaComboBox
      name={name}
      {...textFieldProps}
      // AriaComboBox manages isRequired validation on its own, but we can also have validation information from the
      // surrounding Field component via context.
      isInvalid={isInvalid || undefined}
      aria-labelledby={labelProps?.id ?? undefined}
      menuTrigger="focus"
      className={wrapper}
      {...restProps}
    >
      <InputBuilder
        ref={inputGroupRef}
        {...inputBuilderProps}
        right={(state) => (
          <>
            <RightIcons key="icon" {...state} />
            <ClearButton key="clear" clearButton={clearButton} />
            <ChevronButton key="chevron" />
          </>
        )}
      >
        <AriaInput
          id={inputProps?.id ?? undefined}
          title={title}
          placeholder={placeholder}
        />
      </InputBuilder>
      <Popover triggerRef={inputGroupRef} width={popoverWidth}>
        <ListBox size={inputBuilderProps.size} virtualized={virtualized}>
          {children}
        </ListBox>
      </Popover>
    </AriaComboBox>
  );
}
